import { Config } from 'config/envConfig';
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tabs } from 'antd';
import { Button, Dropdown, Input, LoadingView, alert, AlertType, ConfirmModal } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTrash, faEllipsisV, faEnvelope, faLink, faPlus, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { faTelegram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { ModelTiers, RepoType, InterfaceType, Operation, Features, ChatsFeatures, isFeatureEnabled } from '@quyva/common';
import { useGlobalCredits } from '../context/GlobalStateContext';
import { useFeatureFlags } from '../context';
import axios from 'axios';
import { getUrl } from './pdfDisplay/utils';
import { PersonaPromptsPanel } from './settings/settingsSections/promptSettings/PersonaPromptsPanel';

const buildInitialRow = (id) => ({
    id,
    name: '',
    modelTier: ModelTiers.STANDARD,
    modelName: null,
    prompt: '',
    virtualRepoId: null,
    personaPromptKey: null,
    personaPromptId: null,
});

const VirtualRepositoryModal = ({
    repo,
    setHasUnsavedChanges,
    onEnableTelegramInterface,
    onEnableEmailInterface,
    onEnableWhatsAppInterface,
    fetchAllChatsReposRaw,
    onOpenRepoWebAccessSettings,
    refreshRef,
}) => {
    const { availableCustomModels, isLoadingModels, isAdmin, fetchUserPreferences } =
        useGlobalCredits();
    const { featureFlagsData } = useFeatureFlags();

    // Plan-gating for the interface enable actions (parity: Telegram + WhatsApp).
    const telegramFeatureEnabled = isFeatureEnabled(
        [Features.CHATS, ChatsFeatures.ENABLE_TELEGRAM_BOT],
        featureFlagsData
    );
    const whatsappFeatureEnabled = isFeatureEnabled(
        [Features.CHATS, ChatsFeatures.ENABLE_WHATSAPP],
        featureFlagsData
    );

    const [activeTab, setActiveTab] = useState('configure');
    const [personaHasUnsavedChanges, setPersonaHasUnsavedChanges] = useState(false);
    const [confirmSwitchTabOpen, setConfirmSwitchTabOpen] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);

    const [rows, setRows] = useState([]);
    const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null);
    const [personaPrompts, setPersonaPrompts] = useState([]);
    const [deletedVirtualRepoIds, setDeletedVirtualRepoIds] = useState([]);
    const [isLoadingRepos, setIsLoadingRepos] = useState(false);
    const [reloadCounter, setReloadCounter] = useState(0);

    const currentUserEmailNormalized = useMemo(() => {
        try {
            const cu = JSON.parse(localStorage.getItem('current') || '{}');
            return String(cu?.email || '').trim().toLowerCase();
        } catch (e) {
            return '';
        }
    }, []);

    const isDbRepo =
        repo?.repotype === RepoType.SQLLITE ||
        repo?.repotype === RepoType.POSTGRESS ||
        repo?.repotype === RepoType.MSSQL;

    const buildCategorizedCustomModelSubmenuItems = (
        categorizedModels,
        modelTier,
        handleModelChangeFn
    ) => {
        const submenuItems = [];
        let isFirstCategory = true;

        const sortedCategories = Object.keys(categorizedModels || {}).sort();

        for (const category of sortedCategories) {
            const models = categorizedModels[category];

            if (models && models.length > 0) {
                if (!isFirstCategory) submenuItems.push({ isDivider: true });

                submenuItems.push({
                    label: category,
                    isHeading: true,
                });

                models.forEach((modelName) => {
                    submenuItems.push({
                        label: modelName,
                        action: () => handleModelChangeFn(modelTier, modelName),
                    });
                });

                isFirstCategory = false;
            }
        }

        if (submenuItems.length === 0) {
            return [
                {
                    label: 'No custom models available',
                    disabled: true,
                    action: () => {},
                },
            ];
        }

        return submenuItems;
    };

    // Load virtual repositories from backend when repo changes or reloadCounter updates
    useEffect(() => {
        if (!repo?.repoid) {
            setRows([]);
            setLastSavedSnapshot(null);
            return;
        }

        const fetchVirtualRepos = async () => {
            const baseUrl = `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}`;

            try {
                setIsLoadingRepos(true);

                const url = getUrl(
                    `${baseUrl}/virtual_repo_routes/virtual-repos`
                );

                const response = await axios.get(url, {
                    params: {
                        parent_repo_id: repo.repoid,
                    },
                });

                const data = response?.data || {};
                const list = data.virtual_repos || [];

                const mappedRows = list.map((item, index) => {
                    const displayModel = item.model;
                    const resolvedTier =
                        displayModel === ModelTiers.STANDARD
                            ? ModelTiers.STANDARD
                            : displayModel === ModelTiers.PRECISION
                            ? ModelTiers.PRECISION
                            : ModelTiers.CUSTOM;

                    return {
                        id: index + 1,
                        name: item.virtual_repo_name,
                        modelTier: resolvedTier,
                        modelName: resolvedTier === ModelTiers.CUSTOM ? displayModel : null,
                        prompt: '',
                        personaPromptKey: item.persona_prompt_key,
                        personaPromptId: item.persona_prompt_id ?? null,
                        virtualRepoId: item.virtual_repo_id,
                        telegramEnabled: !!item.telegram_enabled,
                        whatsappEnabled: !!item.whatsapp_enabled,
                        emailInterface: item.email_interface ?? null,
                    };
                });

                // Do not auto-add an empty row; only use backend rows (default row is derived)
                setRows(mappedRows);
                setLastSavedSnapshot(mappedRows);

                // The backend rewrites a retired model on read and returns
                // model_migrated only on the read that performed the rewrite,
                // so this surfaces once per affected virtual repo.
                // Downgrades are recorded in the bell icon by the backend
                // rather than shown as a toast; only the displayed account
                // default needs refreshing here.
                if (list.some((item) => item.model_migrated?.sweep?.user_preference)) {
                    fetchUserPreferences();
                }
            } catch (e) {
                alert(
                    AlertType.ERROR,
                    'Failed to load virtual repositories. Please try again later.'
                );
            } finally {
                setIsLoadingRepos(false);
            }
        };

        fetchVirtualRepos();
    }, [repo?.repoid, reloadCounter]);

    // Fetch persona prompts for dropdown (system + all repo-access users' custom prompts)
    const fetchPersonaPrompts = async () => {
        const baseUrl = `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}`;

        const currentUser = JSON.parse(localStorage.getItem('current') || '{}');

        try {
            const url = getUrl(
                `${baseUrl}/prompts_routes/persona-prompts-for-repo?email=${encodeURIComponent(
                    currentUser?.email || ''
                )}&repoid=${encodeURIComponent(repo?.repoid || '')}`
            );

            const response = await fetch(url);
            const payload = await response.json();

            if (!response.ok) {
                throw new Error(payload?.detail || 'Failed to load persona prompts');
            }

            const system = Array.isArray(payload?.system) ? payload.system : [];
            const users = Array.isArray(payload?.users) ? payload.users : [];

            const currentUserEmail = (currentUser?.email || '').trim().toLowerCase();
            const currentUserDisplayName =
                (currentUser?.username || currentUser?.name || '').trim() || null;

            // Flatten options for internal lookups / defaulting.
            // Use uniqueKey so prompts with same name from different users don't collide.
            const flat = [];
            for (const p of system) {
                if (p.hasOwnProperty('active') && p.active === false) 
                {
                    continue;
                }
                flat.push({
                    uniqueKey: `sys:${p.key}`,
                    key: p.key,
                    id: null,
                    name: p.name,
                    group: 'System Persona Prompts',
                    isHeading: false,
                });
            }

            const sortedUserGroups = [...users].sort((a, b) => {
                const ae = String(a?.email || '').trim().toLowerCase();
                const be = String(b?.email || '').trim().toLowerCase();
                const aIsMe = currentUserEmail && ae === currentUserEmail;
                const bIsMe = currentUserEmail && be === currentUserEmail;
                if (aIsMe && !bIsMe) return -1;
                if (!aIsMe && bIsMe) return 1;
                return ae.localeCompare(be);
            });

            for (const group of sortedUserGroups) {
                const email = group?.email;
                const prompts = Array.isArray(group?.prompts) ? group.prompts : [];
                const groupEmailNormalized = String(email || '').trim().toLowerCase();
                const isCurrentUserGroup =
                    !!currentUserEmail && groupEmailNormalized === currentUserEmail;

                const otherUserDisplayName = String(group?.username || '').trim();
                const displayName = isCurrentUserGroup
                    ? currentUserDisplayName
                    : otherUserDisplayName;

                for (const p of prompts) {
                    if (p.hasOwnProperty('active') && p.active === false) {
                        continue;
                    }
                    flat.push({
                        uniqueKey: `usr:${p.id}`,
                        key: p.key,
                        id: p.id,
                        name: isCurrentUserGroup
                            ? `${p.name}`
                            : displayName
                            ? `${p.name} (${displayName})`
                            : `${p.name}`,
                        group: email,
                        isHeading: false,
                    });
                }
            }

            setPersonaPrompts(flat);

            // Auto-assign the first available persona prompt to any row without a selection
            if (flat.length > 0) {
                const firstPrompt = flat[0];
                setRows((prevRows) =>
                    prevRows.map((r) =>
                        r.personaPromptKey || r.personaPromptId
                            ? r
                            : {
                                  ...r,
                                  personaPromptKey: firstPrompt.id ? null : firstPrompt.key,
                                  personaPromptId: firstPrompt.id ?? null,
                              }
                    )
                );
            }
        } catch (e) {
            alert(
                AlertType.ERROR,
                'Failed to load persona prompts. Please try again later.'
            );
        }
    };

    useEffect(() => {
        fetchPersonaPrompts();
    }, [repo?.repoid]);

    // Expose refresh function to parent via ref
    useEffect(() => {
        if (refreshRef) {
            refreshRef.current = () => {
                setReloadCounter((prev) => prev + 1);
            };
        }
    }, [refreshRef]);

    // Track unsaved changes
    useEffect(() => {
        if (!lastSavedSnapshot) {
            setHasUnsavedChanges(false);
            return;
        }
        const current = JSON.stringify(rows);
        const saved = JSON.stringify(lastSavedSnapshot);
        setHasUnsavedChanges(current !== saved);
    }, [rows, lastSavedSnapshot, setHasUnsavedChanges]);

    const handleModelChangeForRow = (rowId, modelTier, modelName = null) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === rowId
                    ? {
                          ...r,
                          modelTier,
                          modelName: modelTier === ModelTiers.CUSTOM ? modelName : null,
                      }
                    : r
            )
        );
    };

    const handlePromptChange = (rowId, value) => {
        setRows((prev) =>
            prev.map((r) => (r.id === rowId ? { ...r, prompt: value } : r))
        );
    };

    const handleNameChange = (rowId, value) => {
        setRows((prev) =>
            prev.map((r) => (r.id === rowId ? { ...r, name: value } : r))
        );
    };

    const handleAddRow = () => {
        const nextId = rows.length
            ? Math.max(...rows.map((r) => r.id)) + 1
            : 1;
        let newRow = buildInitialRow(nextId);

        // If persona prompts are already loaded, default the new row to the first persona
        if (personaPrompts && personaPrompts.length > 0) {
            const firstPrompt = personaPrompts[0];
            newRow = {
                ...newRow,
                personaPromptKey: firstPrompt.key,
                personaPromptId: firstPrompt.id,
            };
        }

        setRows((prev) => [...prev, newRow]);

        // Ensure the newly added row is brought into view inside the
        // scrollable table body so the user immediately sees it.
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                try {
                    const rowSelector = `tr[data-row-key="${nextId}"]`;
                    const rowEl = document.querySelector(rowSelector);
                    if (rowEl && typeof rowEl.scrollIntoView === 'function') {
                        rowEl.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                    }
                } catch (e) {
                }
            });
        });
    };

    const handleDeleteRow = async (row) => {
        if (row.virtualRepoId) {
            setDeletedVirtualRepoIds((prev) =>
                prev.includes(row.virtualRepoId)
                    ? prev
                    : [...prev, row.virtualRepoId]
            );
        }

        // Ignore delete for any non-backend (safety), though default row is not in rows
        setRows((prev) => prev.filter((r) => r.id !== row.id));
    };

    const handleSave = async () => {
        // Ensure all names are non-empty and unique within this repo (excluding default row, which is not part of rows)
        const seenNames = new Set();
        for (const r of rows) {
            const trimmed = (r.name || '').trim();
            if (!trimmed) {
                alert(
                    AlertType.WARNING,
                    'Virtual repository name cannot be empty.'
                );
                return;
            }

            const key = trimmed.toLowerCase();
            if (seenNames.has(key)) {
                alert(
                    AlertType.WARNING,
                    'Each virtual repository name must be unique for this repository.'
                );
                return;
            }
            seenNames.add(key);
        }

        // Ensure every row has a selected persona prompt before saving
        for (const r of rows) {
            if (!r.personaPromptKey && !r.personaPromptId) {
                alert(
                    AlertType.WARNING,
                    'Please select a persona prompt for every virtual repository before saving.'
                );
                return;
            }
        }

        setLastSavedSnapshot(rows);

        const baseUrl = `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}`;

        const newRows = rows.filter((r) => !r.virtualRepoId);

        // Only send updates for existing rows where something actually changed
        const originalRowsById = new Map(
            (lastSavedSnapshot || [])
                .filter((r) => r.virtualRepoId)
                .map((r) => [r.virtualRepoId, r])
        );

        const existingRowsToUpdate = rows.filter((r) => {
            if (!r.virtualRepoId) return false;
            const original = originalRowsById.get(r.virtualRepoId);
            if (!original) return true;

            const currentModel = r.modelName || r.modelTier;
            const originalModel = original.modelName || original.modelTier;
            const promptChanged =
                r.personaPromptKey !== original.personaPromptKey ||
                (r.personaPromptId ?? null) !== (original.personaPromptId ?? null);
            const modelChanged = currentModel !== originalModel;

            return promptChanged || modelChanged;
        });

        // Bulk add new virtual repos
        if (newRows.length > 0 && repo?.repoid) {
            try {
                const createUrl = getUrl(
                    `${baseUrl}/virtual_repo_routes/add-virtual-repos`
                );

                const payload = {
                    parent_repo_id: repo?.repoid,
                    virtual_repos: newRows.map((row) => ({
                        persona_prompt_key: row.personaPromptId
                            ? null
                            : row.personaPromptKey,
                        persona_prompt_id: row.personaPromptId
                            ? row.personaPromptId
                            : null,
                        model_name:
                            row.modelTier === ModelTiers.CUSTOM
                                ? row.modelName
                                : row.modelTier,
                        repo_name: row.name,
                    })),
                };

                await axios.post(createUrl, payload);
            }
            catch (e) {
                alert(
                    AlertType.ERROR,
                    'Failed to create virtual repositories. Please try again.'
                );
                return;
            }
        }

        // Bulk update existing virtual repos
        if (existingRowsToUpdate.length > 0) {
            try {
                const updateUrl = getUrl(
                    `${baseUrl}/virtual_repo_routes/update-virtual-repos`
                );

                const payload = {
                    updates: existingRowsToUpdate.map((row) => ({
                        virtual_repo_id: row.virtualRepoId,
                        new_persona_prompt_key: row.personaPromptId
                            ? null
                            : row.personaPromptKey,
                        new_persona_prompt_id: row.personaPromptId
                            ? row.personaPromptId
                            : null,
                        new_model_name:
                            row.modelTier === ModelTiers.CUSTOM
                                ? row.modelName
                                : row.modelTier,
                    })),
                };

                await axios.put(updateUrl, payload);
            } catch (e) {
                alert(
                    AlertType.ERROR,
                    'Failed to update virtual repositories. Please try again.'
                );
                return;
            }
        }

        // Bulk delete removed virtual repos
        if (deletedVirtualRepoIds.length > 0) {
            try {
                const deleteUrl = getUrl(
                    `${baseUrl}/virtual_repo_routes/delete-virtual-repos`
                );
                await axios.delete(deleteUrl, {
                    data: { virtual_repo_ids: deletedVirtualRepoIds },
                });
            } catch (e) {
            }
        }

        setDeletedVirtualRepoIds([]);

        alert(AlertType.SUCCESS, 'Virtual repository configuration saved.');

        try {
            window.dispatchEvent(
                new CustomEvent('virtual-repos-updated', {
                    detail: { parent_repo_id: repo?.repoid || null },
                })
            );
        } catch (e) {
        }

        // Trigger a reload so that the modal reflects the latest backend state (e.g., disabled names)
        setReloadCounter((prev) => prev + 1);
    };

    const columns = [
        {
            title: 'Sr. No.',
            key: 'index',
            align: 'left',
            className: 'px-2',
            render: (_text, _record, index) => index + 1,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            align: 'left',
            width: 220,
            className: 'px-2',
            render: (text, record, index) => {
                const getInterfaceEnabled = (type) => {
                    if (type === InterfaceType.TELEGRAM) {
                        return !!record.telegramEnabled;
                    }
                    if (type === InterfaceType.WHATSAPP) {
                        return !!record.whatsappEnabled;
                    }
                    if (type === InterfaceType.EMAIL) {
                        return !!record.emailInterface;
                    }
                    return false;
                };

                const interfaceIconConfigs = [
                    {
                        type: InterfaceType.TELEGRAM,
                        icon: faTelegram,
                        title: 'Telegram interface enabled on this repository',
                        onClick: onEnableTelegramInterface,
                        buildPayload: () => ({
                            ...repo,
                            repoid: record.virtualRepoId,
                            name: record.name,
                            telegramEnabled: !!record.telegramEnabled,
                        }),
                    },
                    {
                        type: InterfaceType.WHATSAPP,
                        icon: faWhatsapp,
                        title: 'WhatsApp interface enabled on this repository',
                        onClick: onEnableWhatsAppInterface,
                        buildPayload: () => ({
                            ...repo,
                            repoid: record.virtualRepoId,
                            name: record.name,
                            whatsappEnabled: !!record.whatsappEnabled,
                        }),
                    },
                    {
                        type: InterfaceType.EMAIL,
                        icon: faEnvelope,
                        title: 'Email interface enabled on this repository',
                        onClick: onEnableEmailInterface,
                        buildPayload: () => ({
                            ...repo,
                            repoid: record.virtualRepoId,
                            name: record.name,
                            emailInterface: record.emailInterface ?? null,
                        }),
                    },
                ];

                const interfaceIconsToRender = interfaceIconConfigs
                    .filter((cfg) => !!cfg.onClick)
                    .filter((cfg) => {
                        return getInterfaceEnabled(cfg.type);
                    })
                    // WhatsApp management is restricted to admins / super-admins.
                    .filter((cfg) => cfg.type !== InterfaceType.WHATSAPP || isAdmin);

                return (
                    <div className='w-full flex items-center gap-2'>
                        <Input
                            type='text'
                            value={text}
                            placeholder={`Option ${index + 1}`}
                            onChange={(e) =>
                                handleNameChange(record.id, e.target.value)
                            }
                            disabled={!!record.virtualRepoId}
                            title={
                                record.virtualRepoId
                                    ? 'This virtual repository name is not editable.'
                                    : undefined
                            }
                            className={`flex-1 rounded-lg px-2 py-1 border border-backgrounds-border focus:outline-backgrounds-primary200 text-sm disabled:bg-backgrounds-secondary disabled:text-texts-secondary300 ${
                                !!record.virtualRepoId
                                    ? 'cursor-not-allowed'
                                    : ''
                            }`}
                        />

                        {interfaceIconsToRender.map((cfg) => (
                            <FontAwesomeIcon
                                key={cfg.type}
                                icon={cfg.icon}
                                className={`text-icons-primary ml-2 drop-shadow-sm cursor-pointer`}
                                title={cfg.title}
                                onClick={async (e) => {
                                    e.stopPropagation();

                                    if (!record.virtualRepoId) {
                                        alert(
                                            AlertType.WARNING,
                                            'Please click Save to create this virtual repository before enabling Email/Telegram/WhatsApp.'
                                        );
                                        return;
                                    }

                                    cfg.onClick(cfg.buildPayload());
                                }}
                            />
                        ))}
                    </div>
                );
            },
        },
        {
            title: 'Model Name',
            dataIndex: 'modelName',
            key: 'modelName',
            align: 'left',
            width: 170,
            className: 'px-2',
            render: (_text, record) => {
                const customSubmenuModelItems =
                    buildCategorizedCustomModelSubmenuItems(
                        availableCustomModels,
                        ModelTiers.CUSTOM,
                        (tier, model) =>
                            handleModelChangeForRow(record.id, tier, model)
                    );

                const modelDropdownItems = [
                    {
                        label: 'Standard',
                        action: () =>
                            handleModelChangeForRow(
                                record.id,
                                ModelTiers.STANDARD,
                                null
                            ),
                    },
                    {
                        label: 'Precision',
                        action: () =>
                            handleModelChangeForRow(
                                record.id,
                                ModelTiers.PRECISION,
                                null
                            ),
                    },
                    {
                        label: 'Custom',
                        isSubmenu: true,
                        items: customSubmenuModelItems,
                    },
                ];

                const buttonText = isLoadingModels
                    ? 'Loading...'
                    : record.modelTier === ModelTiers.CUSTOM && record.modelName
                    ? record.modelName
                    : record.modelTier;

                return (
                    <div className='w-full'>
                        <Dropdown
                            dropDownList={modelDropdownItems}
                            showButtonText={true}
                            buttonText={buttonText}
                            listTitleText='Select AI Model'
                            placement='bottomLeft'
                            buttonClassName={`w-full px-2 py-1 text-xs border border-backgrounds-border rounded-lg truncate text-left ${
                                ''
                            }`}
                        />
                    </div>
                );
            },
        },
        {
            title: 'Persona Prompts',
            key: 'promptName',
            align: 'left',
            width: 260,
            className: 'px-2',
            render: (_text, record) => {
                const baseOptions = personaPrompts || [];

                const sortedOptions = [...baseOptions].sort((a, b) => {
                    const aGroupRaw = a.group || '';
                    const bGroupRaw = b.group || '';

                    const aGroup =
                        aGroupRaw === 'System Persona Prompts'
                            ? 'System Persona Prompts'
                            : String(aGroupRaw).trim().toLowerCase();
                    const bGroup =
                        bGroupRaw === 'System Persona Prompts'
                            ? 'System Persona Prompts'
                            : String(bGroupRaw).trim().toLowerCase();

                    const aRank =
                        aGroup === 'System Persona Prompts'
                            ? 0
                            : aGroup && currentUserEmailNormalized && aGroup === currentUserEmailNormalized
                            ? 1
                            : 2;
                    const bRank =
                        bGroup === 'System Persona Prompts'
                            ? 0
                            : bGroup && currentUserEmailNormalized && bGroup === currentUserEmailNormalized
                            ? 1
                            : 2;

                    if (aRank !== bRank) return aRank - bRank;

                    // For non-system groups, sort groups alphabetically
                    if (aRank === 2 && aGroup !== bGroup) {
                        return String(aGroup).localeCompare(String(bGroup));
                    }

                    return String(a.name || '').localeCompare(String(b.name || ''));
                });

                const mappedKey = record.personaPromptKey || null;
                const mappedId = record.personaPromptId ?? null;
                const mappedUnique = mappedId
                    ? `usr:${mappedId}`
                    : mappedKey
                    ? `sys:${mappedKey}`
                    : null;

                const matched = mappedUnique
                    ? baseOptions.find((p) => p.uniqueKey === mappedUnique)
                    : null;

                const selectedPrompt = matched || baseOptions[0] || null;

                const handlePromptSelect = (uniqueKey) => {
                    const selected = baseOptions.find(
                        (p) => p.uniqueKey === uniqueKey
                    );
                    if (!selected) return;

                    setRows((prev) =>
                        prev.map((r) =>
                            r.id === record.id
                                ? {
                                      ...r,
                                      personaPromptKey: selected.id ? null : selected.key,
                                      personaPromptId: selected.id ?? null,
                                  }
                                : r
                        )
                    );
                };

                const dropdownItems = sortedOptions.map((p) => ({
                    label: p.name,
                    selected: selectedPrompt?.uniqueKey === p.uniqueKey,
                    action: () => handlePromptSelect(p.uniqueKey),
                }));

                return (
                    <div className='w-full'>
                        <Dropdown
                            dropDownList={dropdownItems}
                            showButtonText={true}
                            buttonText={
                                selectedPrompt?.name || 'Select prompt'
                            }
                            listTitleText='Select Persona Prompt'
                            placement='bottomLeft'
                            listClassName='w-[260px] max-w-[260px]'
                            buttonClassName={`w-full px-2 py-1 text-xs border border-backgrounds-border rounded-lg truncate text-left ${
                                ''
                            }`}
                        />
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'left',
            className: 'px-2',
            render: (_text, record) => {
                const actionItems = [];
                const telegramEnabledEffective = !!record.telegramEnabled;

                if (onEnableTelegramInterface && telegramFeatureEnabled) {
                    actionItems.push({
                        label: 'Enable Telegram Interface',
                        icon: faTelegram,
                        action: async () => {
                            if (!record.virtualRepoId) {
                                alert(
                                    AlertType.WARNING,
                                    'Please click Save to create this virtual repository before enabling Telegram.'
                                );
                                return;
                            }

                            onEnableTelegramInterface({
                                ...repo,
                                repoid: record.virtualRepoId,
                                name: record.name,
                                telegramEnabled: telegramEnabledEffective,
                            });
                        },
                    });
                }

                if (onEnableEmailInterface) {
                    actionItems.push({
                        label: 'Enable Email Interface',
                        icon: faEnvelope,
                        action: async () => {
                            if (!record.virtualRepoId) {
                                alert(
                                    AlertType.WARNING,
                                    'Please click Save to create this virtual repository before enabling Email.'
                                );
                                return;
                            }

                            onEnableEmailInterface({
                                ...repo,
                                repoid: record.virtualRepoId,
                                name: record.name,
                            });
                        },
                    });
                }

                if (onEnableWhatsAppInterface && whatsappFeatureEnabled && isAdmin) {
                    actionItems.push({
                        label: 'Enable WhatsApp Interface',
                        icon: faWhatsapp,
                        action: async () => {
                            if (!record.virtualRepoId) {
                                alert(
                                    AlertType.WARNING,
                                    'Please click Save to create this virtual repository before enabling WhatsApp.'
                                );
                                return;
                            }

                            onEnableWhatsAppInterface({
                                ...repo,
                                repoid: record.virtualRepoId,
                                name: record.name,
                            });
                        },
                    });
                }

                if (onOpenRepoWebAccessSettings) {
                    actionItems.push({
                        label: 'Repository Web Access Link',
                        icon: faLink,
                        action: async () => {
                            if (!record.virtualRepoId) {
                                alert(
                                    AlertType.WARNING,
                                    'Please click Save to create this virtual repository before opening Repository Web Access Link.'
                                );
                                return;
                            }

                            onOpenRepoWebAccessSettings({
                                ...repo,
                                repoid: record.virtualRepoId,
                                name: record.name,
                                parent_repo:
                                    repo?.parent_repo ?? repo?.repoid ?? null,
                                parent_repo_id:
                                    repo?.parent_repo ?? repo?.repoid ?? null,
                            });
                        },
                    });
                }

                return (
                    <div className='flex items-center w-full gap-2'>
                        <div className='flex-1 flex items-center gap-2'>
                            {actionItems.length > 0 && (
                                <Dropdown
                                    dropDownList={actionItems}
                                    showButtonText={false}
                                    buttonText={null}
                                    placement='bottomLeft'
                                    icon={
                                        <FontAwesomeIcon
                                            icon={faEllipsisV}
                                            className='text-xs'
                                        />
                                    }
                                    buttonClassName={`w-full h-7 flex items-center justify-between px-2 border border-backgrounds-border rounded-lg ${
                                        ''
                                    }`}
                                />
                            )}
                            <Button
                                type={'danger'}
                                icon={faTrash}
                                hoverText='Delete virtual repository'
                                className={`w-7 h-7 flex items-center justify-center text-[11px] px-0 py-0 ${
                                    ''
                                }`}
                                disabled={false}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleDeleteRow(record);
                                }}
                            />
                        </div>
                    </div>
                );
            },
        },
    ];

    const hasUnsavedChanges =
        !!lastSavedSnapshot &&
        JSON.stringify(rows) !== JSON.stringify(lastSavedSnapshot);

    const requestTabChange = (nextTab) => {
        if (activeTab === nextTab) return;

        if (activeTab === 'persona' && personaHasUnsavedChanges) {
            setPendingTab(nextTab);
            setConfirmSwitchTabOpen(true);
            return;
        }

        setActiveTab(nextTab);
    };

    return (
        <div className='w-[75vw] h-[72vh] flex flex-col'>
            <div className='px-3 pt-2'>
                <Tabs
                    activeKey={activeTab}
                    onChange={(k) => requestTabChange(k)}
                    items={[
                        { key: 'configure', label: 'Configure Repository' },
                        { key: 'persona', label: 'Persona Prompts' },
                    ]}
                />
            </div>

            {activeTab === 'persona' ? (
                <div className='flex-1 min-h-0 mt-2'>
                    <PersonaPromptsPanel
                        isAdmin={!!isAdmin}
                        repoid={repo?.repoid}
                        onDirtyChange={(dirty) =>
                            setPersonaHasUnsavedChanges(!!dirty)
                        }
                        onSaved={() => {
                            fetchPersonaPrompts();
                        }}
                    />
                </div>
            ) : (
                <div className='flex items-center justify-between mb-1 px-3'>
                    <div className='flex items-center space-x-2 text-xs text-texts-secondary300'>
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            iconColor={'blue'}
                            className='text-texts-secondary400'
                        />
                        <span>
                            Configure multiple virtual repository options. You can also assign a Persona Prompt per virtual repo.
                            Manage Persona Prompts in the Persona Prompts tab (changes apply immediately to the dropdown).
                        </span>
                    </div>
                    <div className='flex space-x-2 p-2'>
                        <Button
                            type={'success'}
                            text={'Add Virtual Repo'}
                            icon={faPlus}
                            onClick={handleAddRow}
                            hoverText={
                                isDbRepo
                                    ? 'Virtual repositories are not supported for database repositories.'
                                    : 'Add a new virtual repository configuration'
                            }
                            className='text-xs'
                            disabled={isDbRepo}
                        />
                        <Button
                            type={'success'}
                            text={'Save'}
                            icon={faFloppyDisk}
                            onClick={handleSave}
                            disabled={!hasUnsavedChanges}
                            hoverText='Save virtual repositories'
                            className=' text-xs'
                        />
                    </div>
                </div>
            )}

            {activeTab !== 'persona' && (
                <div className='flex-1 border border-backgrounds-border rounded-xl bg-backgrounds-white overflow-hidden'>
                    {isLoadingRepos ? (
                        <div className='w-full h-full flex items-center justify-center'>
                            <LoadingView text='Loading virtual repositories...' />
                        </div>
                    ) : (
                        <Table
                            dataSource={rows}
                            columns={columns}
                            rowKey='id'
                            pagination={false}
                            scroll={{ y: 'calc(72vh - 140px)' }}
                            className='text-xs'
                        />
                    )}
                </div>
            )}
            {confirmSwitchTabOpen && (
                <ConfirmModal
                    modalText="You have unsaved persona prompt changes. If you switch tabs now, they won't be saved. Do you want to continue?"
                    onConfirm={() => {
                        setConfirmSwitchTabOpen(false);
                        const next = pendingTab;
                        setPendingTab(null);
                        setPersonaHasUnsavedChanges(false);
                        if (next) setActiveTab(next);
                    }}
                    onCancel={() => {
                        setConfirmSwitchTabOpen(false);
                        setPendingTab(null);
                    }}
                    confirmText='Discard Changes'
                    cancelText='Stay'
                />
            )}
        </div>
    );
};

export default VirtualRepositoryModal;
